import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Wifi, WifiOff, ImageOff, Coins, QrCode, Copy, Check, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { useGetMachine } from '@/services/api/endpoints/MachineService';
import { useGetProducts } from '@/services/api/endpoints/ProductService';
import { useGetSessions, useSeedMachineFunds } from '@/services/api/endpoints/SessionService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate, formatDateOnly, formatAmount, formatRelativeTime } from '@/lib/utils';
import { MachineModal } from './MachineModal';
import { MachineConfigPanel } from './MachineConfigPanel';

const MACHINE_TYPE_LABELS: Record<string, string> = {
  esp32: 'ESP32',
  pax_im30: 'PAX IM30',
  pax_im25: 'PAX IM25',
  ciontek_cm30: 'Ciontek CM30',
};

const MDB_ROLE_LABELS: Record<string, string> = {
  mdb_cashless_slave: 'MDB Cashless Slave',
  mdb_master: 'MDB Master',
};

type Tab = 'info' | 'products' | 'sessions' | 'qrcode' | 'config';

const QR_LINK_BASE = import.meta.env.VITE_QR_LINK_BASE || 'https://api.vendpay.it';

export function MachineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<Tab>('info');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedAmount, setSeedAmount] = useState('');
  const [seedReason, setSeedReason] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const seedMutation = useSeedMachineFunds();

  const handleSeed = async () => {
    const amount = Math.round(parseFloat(seedAmount));
    if (!amount || amount <= 0) {
      toast.error(t('machines.seedInvalidAmount') || 'Neispravan iznos');
      return;
    }
    try {
      await seedMutation.mutateAsync({ machineId: id!, amount, reason: seedReason.trim() || undefined });
      toast.success(t('machines.seedSuccess') || `${amount} RSD poslato aparatu`);
      setSeedOpen(false);
      setSeedAmount('');
      setSeedReason('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Greška');
    }
  };

  const { data: machine, isLoading } = useGetMachine(id!);
  const { data: productsData } = useGetProducts(
    productsPage, 20, undefined, 'slotNumber', 'asc', id
  );
  const { data: sessionsData } = useGetSessions(
    sessionsPage, 10, undefined, 'createdAt', 'desc', id
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'info', label: t('companies.info') },
    { key: 'products', label: t('products.title') },
    { key: 'sessions', label: t('sessions.title') },
    { key: 'qrcode', label: t('machines.qrCode') },
    { key: 'config', label: t('machines.config') },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        {t('common.noResults')}
      </div>
    );
  }

  const isOnline = machine.connectionStatus === 'online';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/machines')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('common.back')}
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{machine.name}</h1>
          <p className="text-sm text-muted-foreground">{machine.code} &middot; {machine.deviceId}</p>
        </div>
        <Badge variant={isOnline ? 'default' : 'secondary'} className="gap-1">
          {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          {machine.connectionStatus}
        </Badge>
        <StatusBadge status={machine.status} />
        <Button variant="outline" size="sm" onClick={() => setSeedOpen(true)} className="text-amber-600 hover:text-amber-700">
          <Coins className="w-4 h-4 mr-1" />
          {t('machines.seedCredit') || 'Dodaj kredit'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEditModalOpen(true)}>
          <Pencil className="w-4 h-4 mr-1" />
          {t('common.edit')}
        </Button>
      </div>

      {/* Seed Credit Dialog */}
      <Dialog open={seedOpen} onOpenChange={setSeedOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('machines.seedCreditTitle') || 'Dodaj kredit aparatu'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t('machines.seedCreditHelp') || `Aparat "${machine.name}" će dobiti ovaj iznos kao kredit. Sledeći korisnik može da ga iskoristi. Važi 60 minuta.`}
            </p>
            <div className="space-y-2">
              <Label>{t('machines.amount') || 'Iznos'} (RSD)</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={seedAmount}
                onChange={e => setSeedAmount(e.target.value)}
                placeholder="0"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>{t('machines.reason') || 'Razlog'} ({t('common.optional') || 'opciono'})</Label>
              <Input
                value={seedReason}
                onChange={e => setSeedReason(e.target.value)}
                placeholder={t('machines.reasonPlaceholder') || 'npr. kvar aparata, refundacija...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSeed}
              disabled={seedMutation.isPending || !seedAmount}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {seedMutation.isPending ? (t('common.saving') || 'Slanje...') : (t('machines.seedConfirm') || 'Pošalji')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('machines.machineDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoRow label={t('machines.name')} value={machine.name} />
              <InfoRow label={t('machines.code')} value={machine.code} />
              <InfoRow label={t('machines.deviceId')} value={<span className="font-mono text-xs">{machine.deviceId}</span>} />
              <InfoRow label={t('machines.company')} value={machine.company?.name || '-'} />
              <InfoRow
                label={t('machines.type')}
                value={machine.type ? (
                  <Badge variant="outline">{MACHINE_TYPE_LABELS[machine.type] || machine.type}</Badge>
                ) : '-'}
              />
              <InfoRow
                label={t('machines.mdbRole')}
                value={machine.mdbRole ? (
                  <Badge variant="outline">{MDB_ROLE_LABELS[machine.mdbRole] || machine.mdbRole}</Badge>
                ) : '-'}
              />
              <InfoRow label={t('common.status')} value={<StatusBadge status={machine.status} />} />
              <InfoRow
                label={t('machines.connectionStatus')}
                value={
                  <Badge variant={isOnline ? 'default' : 'secondary'}>
                    {machine.connectionStatus}
                  </Badge>
                }
              />
              <InfoRow
                label={t('machines.lastSeen')}
                value={machine.lastSeenAt ? formatRelativeTime(machine.lastSeenAt) : '-'}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('settings.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoRow label={t('machines.latitude')} value={machine.latitude?.toString() || '-'} />
              <InfoRow label={t('machines.longitude')} value={machine.longitude?.toString() || '-'} />
              <InfoRow label={t('machines.geofenceRadius')} value={`${machine.geofenceRadius} m`} />
              <InfoRow label={t('machines.maxPrice')} value={formatAmount(machine.maxPrice)} />
              <InfoRow label={t('products.title')} value={machine.products?.length || productsData?.pagination.total || 0} />
              <InfoRow label={t('users.createdAt')} value={formatDateOnly(machine.createdAt)} />
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'products' && (
        <Card>
          <CardContent className="p-0">
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="w-14 p-4"></th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('products.slotNumber')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('products.name')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('products.price')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productsData?.items.map(p => (
                      <tr key={p.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          {p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name} className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                              <ImageOff className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-sm text-center font-medium">{p.slotNumber}</td>
                        <td className="p-4 text-sm">{p.name}</td>
                        <td className="p-4 text-sm text-right font-medium">{formatAmount(p.price)}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={p.status} />
                        </td>
                      </tr>
                    ))}
                    {productsData?.items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">{t('common.noResults')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {productsData?.items.map(p => (
                  <div key={p.id} className="p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.name} className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                          <ImageOff className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <span className="text-sm font-medium">{p.name}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('products.slotNumber')}: {p.slotNumber}</span>
                      <span className="font-medium">{formatAmount(p.price)}</span>
                    </div>
                    <div>
                      <StatusBadge status={p.status} />
                    </div>
                  </div>
                ))}
                {productsData?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">{t('common.noResults')}</div>
                )}
              </div>
            </>
          </CardContent>
          {productsData?.pagination && productsData.pagination.totalPages > 1 && (
            <div className="p-4">
              <Pagination page={productsPage} totalPages={productsData.pagination.totalPages} onPageChange={setProductsPage} />
            </div>
          )}
        </Card>
      )}

      {activeTab === 'sessions' && (
        <Card>
          <CardContent className="p-0">
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('sessions.date')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('sessions.user')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('sessions.reservedAmount')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsData?.items.map(s => (
                      <tr key={s.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm">{formatDate(s.createdAt)}</td>
                        <td className="p-4 text-sm">{s.user?.email || '-'}</td>
                        <td className="p-4 text-sm text-right font-medium">{formatAmount(s.reservedAmount)}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={s.status} />
                        </td>
                      </tr>
                    ))}
                    {sessionsData?.items.length === 0 && (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-muted-foreground">{t('common.noResults')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {sessionsData?.items.map(s => (
                  <div key={s.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDate(s.createdAt)}</span>
                      <StatusBadge status={s.status} />
                    </div>
                    <div className="text-sm">{s.user?.email || '-'}</div>
                    <div className="text-sm font-medium">{formatAmount(s.reservedAmount)}</div>
                  </div>
                ))}
                {sessionsData?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">{t('common.noResults')}</div>
                )}
              </div>
            </>
          </CardContent>
          {sessionsData?.pagination && sessionsData.pagination.totalPages > 1 && (
            <div className="p-4">
              <Pagination page={sessionsPage} totalPages={sessionsData.pagination.totalPages} onPageChange={setSessionsPage} />
            </div>
          )}
        </Card>
      )}

      {activeTab === 'qrcode' && (() => {
        const qrLink = machine.code ? `${QR_LINK_BASE}/link?m=${machine.code}` : '';
        const qrImg = (size: number) =>
          `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(qrLink)}&format=png`;
        const copyLink = async () => {
          if (!qrLink) return;
          try {
            await navigator.clipboard.writeText(qrLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
          } catch {
            toast.error(t('common.copyFailed') || 'Kopiranje nije uspelo');
          }
        };
        const downloadPng = () => {
          if (!qrLink) return;
          const link = document.createElement('a');
          link.href = qrImg(400);
          link.download = `qr-${machine.code}.png`;
          link.target = '_blank';
          link.click();
        };
        const printQr = () => {
          if (!qrLink) return;
          const w = window.open('', '_blank');
          if (!w) return;
          w.document.write(`<html><head><title>QR ${machine.code}</title><style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:system-ui,sans-serif}img{width:300px;height:300px}.code{font-size:32px;font-weight:bold;letter-spacing:4px;margin-top:20px;font-family:monospace}.name{font-size:18px;color:#666;margin-top:10px}</style></head><body><img src="${qrImg(400)}" alt="QR"/><div class="code">${machine.code}</div><div class="name">${machine.name}</div><script>window.onload=function(){window.print()}</script></body></html>`);
          w.document.close();
        };
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-primary" />
                  {t('machines.qrCode')}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {qrLink ? (
                  <>
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                      <img
                        src={qrImg(250)}
                        alt={`QR ${machine.code}`}
                        className="w-48 h-48 sm:w-64 sm:h-64"
                      />
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-xs text-muted-foreground mb-2">{t('machines.code')}</p>
                      <code className="px-4 py-2 sm:px-6 sm:py-3 bg-background border-2 border-primary/20 rounded-xl font-mono text-xl sm:text-3xl font-bold tracking-widest text-primary">
                        {machine.code}
                      </code>
                    </div>
                  </>
                ) : (
                  <div className="w-48 h-48 sm:w-64 sm:h-64 bg-muted rounded-xl flex items-center justify-center">
                    <p className="text-muted-foreground text-sm text-center px-4">
                      {t('machines.qrNoCode')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('machines.qrEncodedLink')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted border rounded-lg text-sm break-all font-mono">
                    {qrLink || '-'}
                  </code>
                  {qrLink && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyLink}
                      className="shrink-0"
                      title={t('common.copy') || 'Kopiraj'}
                    >
                      {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
                {qrLink && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button variant="outline" className="flex-1 gap-2" onClick={downloadPng}>
                      <Download className="w-4 h-4" />
                      {t('machines.qrDownload')}
                    </Button>
                    <Button variant="outline" className="flex-1 gap-2" onClick={printQr}>
                      <Printer className="w-4 h-4" />
                      {t('machines.qrPrint')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {activeTab === 'config' && (
        <MachineConfigPanel machineId={id!} />
      )}

      <MachineModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        machine={machine}
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
